import { Button, CircularProgress, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import Box from "@mui/material/Box";
import { grey, lime } from "@mui/material/colors";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { PieChart } from "@mui/x-charts";
import { GlobalState } from "context/GlobalContext";
import { arrType, converAttrToDatagridCol, createdByColumns, datatypes, inputTypes, objType, primaryKeyColumn } from "data/globalData";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	allOption,
	replenishmentDrawerElements,
	replenishmentParamName,
	replenishmentSelectedJobPath,
	replenishmentSelectedProfilePath,
	selectOptKeyName,
} from "../../data";
import { DataGrid } from "@mui/x-data-grid";
import { Add, DownloadRounded } from "@mui/icons-material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { replJobsColumns } from "./data";
import axios from "axios";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import _ from "lodash";
import moment from "moment";

const recordsLimit = 20;

const ReplJobs = ({ jobs, setJobs, fetchReplJobs }) => {
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const location = useLocation();
	const navigate = useNavigate();

	const [tableRows, setTableRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [offset, setOffset] = useState(0);
	const [hasMore, setHasMore] = useState(true);

	const gridRef = useRef(null); // Ref to track DataGrid scroll

	const columns = [
		{
			field: "created_at",
			headerName: "Created",
			align: "center",
			headerAlign: "center",
			sortable: false,
			renderCell: ({ row, field }) => {
				if (row[field]) return moment(row[field]).format("DD/MM/YY  HH:mm");
				else return "";
			},
			flex: 1,
			minWidth: 120,
		},
		...Object.values(replJobsColumns).map((row) => converAttrToDatagridCol(row)),

		{
			field: "completed",
			headerName: "Status",
			align: "center",
			headerAlign: "center",
			sortable: false,
			renderCell: ({ row, field }) => {
				if (row[field] === true) return "Closed";
				else return "Running";
			},
			flex: 1,
			minWidth: 120,
		},
		{
			field: "progress",
			headerName: "Stage",
			align: "center",
			headerAlign: "center",
			sortable: false,
			renderCell: ({ row: data, field }) => {
				const row = data[field];
				return <span style={{ textTransform: "capitalize" }}>{row.at(-1) ? row.at(-1)?.label?.toLowerCase() : ""}</span>;
			},
			flex: 1,
			minWidth: 120,
		},
	];

	const loadMoreData = useCallback(
		_.throttle(async () => {
			if (loading || !hasMore) return;
			if (offset === 0) {
				setLoadingOpen(true);
			} else setLoading(true);

			const newRows = await fetchReplJobs(offset, recordsLimit);
			setTableRows((prev) => [...prev, ...newRows]);

			setOffset((prevOffset) => prevOffset + recordsLimit);

			if (offset < recordsLimit) setLoadingOpen(false);
			else setLoading(false);

			if (newRows.length < recordsLimit) {
				setHasMore(false); // Stop loading if no more data
			}
		}, 1000), // Throttled to run at most once per second
		[loading, offset, hasMore]
	);

	useEffect(() => {
		const fetch = async () => {
			setLoadingOpen(true);
			let arr = await fetchReplJobs(offset, recordsLimit);
			if (arr) {
				setTableRows(arr);
				if (typeof setJobs === "function") setJobs(arr);
				setLoadingOpen(false);
			}
		};
		tableRows?.length <= 0 && fetch();
	}, [location.search]);

	// Attach scroll event to detect when reaching bottom
	useEffect(() => {
		console.log("loading second useeffect");

		const handleScroll = () => {
			const gridContainer = gridRef.current?.querySelector(".MuiDataGrid-virtualScroller");
			if (!gridContainer) return;

			const { scrollTop, scrollHeight, clientHeight } = gridContainer;
			if (scrollHeight - scrollTop <= clientHeight + 20) {
				loadMoreData(); // Load more when near bottom
			}
		};

		const gridContainer = gridRef.current?.querySelector(".MuiDataGrid-virtualScroller");
		if (gridContainer) {
			gridContainer.addEventListener("scroll", handleScroll);
		}

		return () => {
			if (gridContainer) {
				gridContainer.removeEventListener("scroll", handleScroll);
			}
		};
	}, [loadMoreData]);

	const handleProfileSelect = (row) => {
		navigate(`/${replenishmentSelectedJobPath}?id=${row.job_id}`);
	};

	return (
		<Stack spacing={2} margin="auto">
			<Box width="100%" ref={gridRef}>
				<DataGrid
					rows={jobs?.length ? jobs : tableRows}
					// getRowId={(row) => row?.SITECODE}
					columns={columns}
					sx={{
						width: "100%",
						border: "none",
						"& .MuiDataGrid-row": {
							// borderBottom: "1px solid lightgray",
							cursor: "pointer",
							border: "none",
						},
						"& .MuiDataGrid-cell": {
							// borderBottom: "1px solid lightgray",
							padding: 0,
						},
						"& .MuiDataGrid-columnHeader": {
							cursor: "pointer",
							background: grey[300],
						},
					}}
					getRowId={(row) => row.job_id}
					columnHeaderHeight={35}
					rowHeight={40}
					disableAutosize
					disableRowSelectionOnClick
					pagination={false} // Remove footer
					hideFooter={true}
					disableColumnMenu
					disableColumnFilter
					disableColumnSelector
					onRowClick={({ row }) => handleProfileSelect(row)}
				/>
				{loading && (
					<Box display="flex" sx={{ background: grey[400], borderRadius: 2, p: 1 }} alignItems="center">
						<Typography variant="body2" color={grey[900]}>
							Loading...{" "}
						</Typography>
						<CircularProgress size="18px" sx={{ transform: "scale(0.4)" }} />
					</Box>
				)}
			</Box>
		</Stack>
	);
};

export default ReplJobs;
